import React, { useState, useRef, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Container } from '@mui/material';
import { SkillSheetMainStepperProvider, useSkillSheetMainStepper } from '../../context/SkillSheetMainStepperContext';
import ProjectInfoForm from '../../form/ProjectInfoForm';
import CategoryContentForm from '../../form/CategoryContentForm';
import LanguageFrameworkForm from '../../form/LanguageFrameworkForm';
import BusinessSystemForm from '../../form/BusinessSystemForm';
import CloudServiceInfoForm from '../../form/CloudServiceInfoForm';
import ServerOperatingSystemForm from '../../form/ServerOperatingSystemForm';
import MiddlewareInfoForm from '../../form/MiddlewareInfoForm';
import MultiSelectForm from '../../form/MultiSelectForm';

const steps = [
  'プロジェクト情報',
  '要件定義',
  '基本設計',
  '詳細設計',
  '製造/構築',
  'テスト',
  '運用/保守',
  'フロントエンド',
  'バックエンド',
  '業務システム',
  'クラウド',
  'Server OS',
  'クライアントOS',
  'MW',
  'DB',
  'Network',
  'デザイン',
  'ツール'
];

// メインコンポーネント（ProviderでラップされたContext内部）
function SkillSheetMainEditStepperContent() {
  const [activeStep, setActiveStep] = useState(0);
  const stepperContainerRef = useRef(null);
  const { runStepValidation } = useSkillSheetMainStepper();

  // コンポーネントマウント時にスクロール位置を一番左に設定
  useEffect(() => {
    if (stepperContainerRef.current) {
      stepperContainerRef.current.scrollTo({
        left: 0,
        behavior: 'auto'
      });
    }
  }, []);

  // ステップ移動したときにステップバーのスクロール位置を調整する
  useEffect(() => {
    if (stepperContainerRef.current) {
      const stepperElement = stepperContainerRef.current;
      const containerWidth = stepperElement.offsetWidth;
      const stepWidth = 120;
      const activeStepPosition = activeStep * stepWidth;
      const scrollPosition = activeStepPosition - (containerWidth / 2) + (stepWidth / 2);

      stepperElement.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, [activeStep]);

  // ステップ移動したときにページの最上部までスクロール
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [activeStep]);

  // ステップ移動のバリデーション
  const handleStepChange = (newStep) => {
    const isValid = runStepValidation(activeStep);

    if (isValid) {
      setActiveStep(newStep);
    }
    // バリデーションエラーがある場合は移動しない
  };

  const handleNext = () => {
    handleStepChange(activeStep + 1);
  };

  const handleBack = () => {
    // 前へボタンでもバリデーションを実行
    handleStepChange(activeStep - 1);
  };

  const handleStepClick = (step) => {
    handleStepChange(step);
  };

  // ステップごとの入力フォームを表示
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <ProjectInfoForm isEdit={true} />;
      case 1:
        return (
          <CategoryContentForm
            title="要件定義"
            fieldName="requirements_definition_operations"
            masterDataKey="requirements_definition_operations"
            stepIndex={1}
            isEdit={true}
          />
        );
      case 2:
        return (
          <CategoryContentForm
            title="基本設計"
            fieldName="basic_design_operations"
            masterDataKey="basic_design_operations"
            stepIndex={2}
            isEdit={true}
          />
        );
      case 3:
        return (
          <CategoryContentForm
            title="詳細設計"
            fieldName="detail_design_operations"
            masterDataKey="detail_design_operations"
            stepIndex={3}
            isEdit={true}
          />
        );
      case 4:
        return (
          <CategoryContentForm
            title="製造/構築"
            fieldName="program_operations"
            masterDataKey="program_operations"
            stepIndex={4}
            isEdit={true}
          />
        );
      case 5:
        return (
          <CategoryContentForm
            title="テスト"
            fieldName="test_operations"
            masterDataKey="test_operations"
            stepIndex={5}
            isEdit={true}
          />
        );
      case 6:
        return (
          <CategoryContentForm
            title="運用/保守"
            fieldName="maintenance_operations"
            masterDataKey="maintenance_operations"
            stepIndex={6}
            isEdit={true}
          />
        );
      case 7:
        return (
          <LanguageFrameworkForm
            title="フロントエンド"
            fieldName="frontend_languages"
            masterDataKey="frontend_languages"
            frameworkType="front_end"
            stepIndex={7}
            isEdit={true}
          />
        );
      case 8:
        return (
          <LanguageFrameworkForm
            title="バックエンド"
            fieldName="backend_languages"
            masterDataKey="backend_languages"
            frameworkType="back_end"
            stepIndex={8}
            isEdit={true}
          />
        );
      case 9:
        return <BusinessSystemForm isEdit={true} />;
      case 10:
        return <CloudServiceInfoForm isEdit={true} />;
      case 11:
        return <ServerOperatingSystemForm isEdit={true} />;
      case 12:
        return (
          <MultiSelectForm
            title="クライアントOS"
            fieldName="selectedOperatingSystems"
            masterDataKey="operating_systems"
            backendFieldName="operating_systems"
            stepIndex={12}
            placeholder="クライアントOSを選択または入力してください"
            isEdit={true}
          />
        );
      case 13:
        return <MiddlewareInfoForm isEdit={true} />;
      case 14:
        return (
          <MultiSelectForm
            title="DB"
            fieldName="selectedDbs"
            masterDataKey="dbs"
            backendFieldName="dbs"
            stepIndex={14}
            placeholder="データベースを選択または入力してください"
            isEdit={true}
          />
        );
      case 15:
        return (
          <MultiSelectForm
            title="Network"
            fieldName="selectedNetworks"
            masterDataKey="networks"
            backendFieldName="networks"
            stepIndex={15}
            placeholder="ネットワークを選択または入力してください"
            isEdit={true}
          />
        );
      case 16:
        return (
          <MultiSelectForm
            title="デザイン"
            fieldName="selectedDesignTools"
            masterDataKey="design_tools"
            backendFieldName="design_tools"
            stepIndex={16}
            placeholder="デザインツールを選択または入力してください"
            isEdit={true}
          />
        );
      case 17:
        return (
          <MultiSelectForm
            title="ツール"
            fieldName="selectedTools"
            masterDataKey="tools"
            backendFieldName="tools"
            stepIndex={17}
            placeholder="ツールを選択または入力してください"
            showSaveButton={true}
            isEdit={true}
          />
        );
      default:
        return '不明なステップ';
    }
  };

  // ステップ移動時にスクロール位置をリセット
  const formContainerRef = useRef(null);

  useEffect(() => {
    if (formContainerRef.current) {
      formContainerRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  return (
    <Container maxWidth="md">
      <Box sx={{ width: '100%' }}>
        {/* ステップ表示部分 */}
        <Box
          ref={stepperContainerRef}
          sx={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            mb: 4,
            pb: 1,
            // スクロールバーのスタイリング
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f5f5f5',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#d4d4d8',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: '#a1a1aa',
              },
            },
          }}
        >
          <Stepper
            activeStep={activeStep}
            nonLinear={true}
            orientation="horizontal"
            sx={{
              // ステップ全体のコンテナ
              minWidth: `${steps.length * 120}px`,
              width: `${steps.length * 120}px`,
              '& .MuiStepper-root': {
                padding: 0,
              },
              // コネクターライン（ステップ間の線）
              '& .MuiStepConnector-root': {
                flex: '1 1 auto',
                minWidth: '20px', // connectorの最小幅を明示的に指定
                '& .MuiStepConnector-line': {
                  borderTopWidth: '2px',
                  borderTopStyle: 'solid',
                  minHeight: '2px',
                  width: '100%', // 線の幅を100%に設定
                },
              },
              // アクティブ/完了したステップのコネクター
              '& .MuiStepConnector-active .MuiStepConnector-line, & .MuiStepConnector-completed .MuiStepConnector-line': {
                borderColor: '#4F46E5',
              },
              // ステップコンテナ
              '& .MuiStep-root': {
                flex: '1 1 auto', // connectorが表示されるように変更
                minWidth: '100px', // 最小幅を指定
                padding: '0 2px',
              },
              // ステップアイコン
              '& .MuiStepIcon-root': {
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                '&.Mui-active': {
                  color: '#4F46E5',
                  backgroundColor: '#4F46E5',
                  '& .MuiStepIcon-text': {
                    fill: 'white',
                  },
                },
                '&.Mui-completed': {
                  color: '#4F46E5',
                  backgroundColor: '#4F46E5',
                  '& .MuiStepIcon-text': {
                    fill: 'white',
                  },
                },
                '& .MuiStepIcon-text': {
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  fill: 'white',
                },
              },
              // ステップラベル
              '& .MuiStepLabel-root': {
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                '&:hover': {
                  opacity: 0.8,
                },
              },
              '& .MuiStepLabel-iconContainer': {
                paddingRight: 0,
                marginBottom: '8px',
              },
              '& .MuiStepLabel-labelContainer': {
                width: '100%',
                textAlign: 'center',
              },
              '& .MuiStepLabel-label': {
                fontSize: '0.75rem',
                lineHeight: '1.2',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100px', // ラベル幅も調整
                margin: '0 auto',
                '&.Mui-active': {
                  color: '#4F46E5',
                  fontWeight: 'bold',
                },
                '&.Mui-completed': {
                  color: '#4F46E5',
                },
              },
            }}
          >
            {steps.map((label, index) => (
              <Step key={label} completed={false}>
                <StepLabel
                  onClick={() => handleStepClick(index)}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* フォーム表示部分 */}
        {activeStep !== steps.length && (
          <Box
            ref={formContainerRef}
            sx={{
              bgcolor: 'white',
              p: 3,
              borderRadius: 2,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* ステップごとの入力フォームを表示 */}
            {getStepContent(activeStep)}

            {/* ナビゲーションボタン */}
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3, mt: 3, borderTop: '1px solid #e2e8f0' }}>
              {activeStep !== 0 && (
                <Button
                  variant="contained"
                  onClick={handleBack}
                  sx={{
                    mr: 1,
                    bgcolor: '#dc2626',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#b91c1c',
                    },
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    fontWeight: 'bold'
                  }}
                >
                  前へ
                </Button>
              )}
              <Box sx={{ flex: '1 1 auto' }} />
              {activeStep !== steps.length - 1 && (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  sx={{
                    bgcolor: '#4F46E5',
                    '&:hover': { bgcolor: '#4338CA' },
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    fontWeight: 'bold'
                  }}
                >
                  次へ
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
}

// Providerでラップした外側のコンポーネント
export default function SkillSheetMainEditStepper(props) {
  const { csrfToken, formAction, workHistory } = props;

  return (
    <SkillSheetMainStepperProvider csrfToken={csrfToken} formAction={formAction} workHistory={workHistory}>
      <SkillSheetMainEditStepperContent />
    </SkillSheetMainStepperProvider>
  );
}
